import {
  DEFAULT_DATE_RANGE_PRESET,
  getDateRangeForPreset,
  type DateRangePreset,
} from '../../utils/dateRangePresets';
import type { BillPaymentMethod } from '../../types/billPayment';

export type StatusFilter = 'all' | 'open' | 'overdue' | 'draft' | 'partial' | 'paid' | 'voided';
export type TypeFilter = 'all' | 'bill' | 'bill_payment';
export type PaymentMethodFilter = '' | BillPaymentMethod;
export type SortKey =
  | 'transaction_id'
  | 'transaction_type'
  | 'number'
  | 'txn_date'
  | 'vendor_name'
  | 'total_amount'
  | 'balance'
  | 'status'
  | 'payment_method';
export type SortDir = 'asc' | 'desc';

export type PurchaseTransactionFilters = {
  statusFilter: StatusFilter;
  typeFilter: TypeFilter;
  paymentMethodFilter: PaymentMethodFilter;
  vendorFilter: string;
  page: number;
  sortKey: SortKey;
  sortDir: SortDir;
  datePreset: DateRangePreset;
  startDate: string;
  endDate: string;
  searchQuery: string;
};

const defaultDateRange = getDateRangeForPreset(DEFAULT_DATE_RANGE_PRESET);

export const DEFAULT_PURCHASE_TRANSACTION_FILTERS: PurchaseTransactionFilters = {
  statusFilter: 'all',
  typeFilter: 'all',
  paymentMethodFilter: '',
  vendorFilter: '',
  page: 1,
  sortKey: 'txn_date',
  sortDir: 'desc',
  datePreset: DEFAULT_DATE_RANGE_PRESET,
  startDate: defaultDateRange.startDate,
  endDate: defaultDateRange.endDate,
  searchQuery: '',
};

const STATUS_VALUES: StatusFilter[] = [
  'all',
  'open',
  'overdue',
  'draft',
  'partial',
  'paid',
  'voided',
];

const TYPE_VALUES: TypeFilter[] = ['all', 'bill', 'bill_payment'];

const SORT_KEYS: SortKey[] = [
  'transaction_id',
  'transaction_type',
  'number',
  'txn_date',
  'vendor_name',
  'total_amount',
  'balance',
  'status',
  'payment_method',
];

const PAYMENT_METHODS: BillPaymentMethod[] = [
  'CASH',
  'CARD',
  'BANK_TRANSFER',
  'CHEQUE',
  'ONLINE',
];

const DATE_PRESETS: DateRangePreset[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'last_30_days',
  'this_quarter',
  'last_quarter',
  'last_3_months',
  'last_6_months',
  'last_12_months',
  'this_year',
  'last_year',
  'all',
  'custom',
];

const parseEnum = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const parsePurchaseTransactionFilters = (
  params: URLSearchParams
): PurchaseTransactionFilters => {
  const datePreset = parseEnum(params.get('preset'), DATE_PRESETS, DEFAULT_DATE_RANGE_PRESET);

  let startDate = defaultDateRange.startDate;
  let endDate = defaultDateRange.endDate;

  if (datePreset === 'custom') {
    startDate = params.get('from') ?? '';
    endDate = params.get('to') ?? '';
  } else {
    const range = getDateRangeForPreset(datePreset);
    startDate = range.startDate;
    endDate = range.endDate;
  }

  return {
    statusFilter: parseEnum(params.get('status'), STATUS_VALUES, 'all'),
    typeFilter: parseEnum(params.get('type'), TYPE_VALUES, 'all'),
    paymentMethodFilter: (() => {
      const method = params.get('method');
      if (!method) return '';
      return PAYMENT_METHODS.includes(method as BillPaymentMethod)
        ? (method as BillPaymentMethod)
        : '';
    })(),
    vendorFilter: params.get('vendor')?.trim() ?? '',
    page: parsePositiveInt(params.get('page'), 1),
    sortKey: parseEnum(params.get('sort'), SORT_KEYS, 'txn_date'),
    sortDir: params.get('dir') === 'asc' ? 'asc' : 'desc',
    datePreset,
    startDate,
    endDate,
    searchQuery: params.get('q')?.trim() ?? '',
  };
};

const isDefault = <K extends keyof PurchaseTransactionFilters>(
  key: K,
  value: PurchaseTransactionFilters[K]
) => value === DEFAULT_PURCHASE_TRANSACTION_FILTERS[key];

export const buildPurchaseTransactionSearchParams = (
  filters: PurchaseTransactionFilters
): URLSearchParams => {
  const params = new URLSearchParams();

  if (!isDefault('statusFilter', filters.statusFilter)) {
    params.set('status', filters.statusFilter);
  }
  if (!isDefault('typeFilter', filters.typeFilter)) {
    params.set('type', filters.typeFilter);
  }
  if (filters.paymentMethodFilter) {
    params.set('method', filters.paymentMethodFilter);
  }
  if (filters.vendorFilter) {
    params.set('vendor', filters.vendorFilter);
  }
  if (!isDefault('page', filters.page)) {
    params.set('page', String(filters.page));
  }
  if (!isDefault('sortKey', filters.sortKey)) {
    params.set('sort', filters.sortKey);
  }
  if (!isDefault('sortDir', filters.sortDir)) {
    params.set('dir', filters.sortDir);
  }
  if (!isDefault('datePreset', filters.datePreset)) {
    params.set('preset', filters.datePreset);
  }
  if (filters.datePreset === 'custom') {
    if (filters.startDate) params.set('from', filters.startDate);
    if (filters.endDate) params.set('to', filters.endDate);
  }
  if (filters.searchQuery) {
    params.set('q', filters.searchQuery);
  }

  return params;
};

export const mergePurchaseTransactionFilters = (
  current: URLSearchParams,
  patch: Partial<PurchaseTransactionFilters>
): URLSearchParams => {
  const merged: PurchaseTransactionFilters = {
    ...parsePurchaseTransactionFilters(current),
    ...patch,
  };

  if (patch.datePreset != null && patch.datePreset !== 'custom' && patch.startDate == null) {
    const range = getDateRangeForPreset(patch.datePreset);
    merged.startDate = range.startDate;
    merged.endDate = range.endDate;
  }

  return buildPurchaseTransactionSearchParams(merged);
};
